import _ from 'lodash';
import cleanDeep from 'clean-deep';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import { filteringTable, formatCellValue } from './utils/adminPortalHelpers.js';
import { Actions as ADMINACTIONS } from '../../store/actions/adminActions.js';
import { Actions as ORGANISATIONACTIONS } from '../../store/actions/organisationActions.js';
import { convertToTitleCase, itemToOptions, removeCamelCase } from '../utils/GlobalFunctions.js';

import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown';
import { Check, X, MoreHorizontal, Eye, Pencil, Trash2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, ArrowUp, ArrowDown } from 'lucide-react';

const AdminPortalTable = ({
  table,
  profile,
  branding,
  toggleModal,
  fetchedData,
  tableActions,
  tableColumns,
  tableFilters,
  stateVariables,
  searchableFields,
  getAllItemsFromDB,
  fetchDataFromOtherTables,
  getItemsByTermsFromIndex,
  adminPortalItems,
  setCurrentOrganisation,
}) => {
  const history = useHistory();
  const { headers } = branding?.fonts[0]?.font || {};
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtering, setFiltering] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    (async () => {
      setLoading(true);
      setColumns(tableColumns);
      try {
        if (!fetchedData || fetchedData.length < 1) {
          if (table === 'currency-rates') {
            const { data: items = {} } = await getAllItemsFromDB(table);
            const response = await fetch(items?.items);
            const data = await response.json();
            adminPortalItems({ items: data }, table);
            setFilteredData(data);
          } else {
            const { data: items = {} } = await getAllItemsFromDB(table);
            setFilteredData(items && items?.items);
          }
        }
        if (fetchDataFromOtherTables && fetchDataFromOtherTables.length) {
          fetchDataFromOtherTables.forEach(async (tableItem) => await getAllItemsFromDB(tableItem));
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [table, tableColumns?.length, fetchedData?.length, fetchDataFromOtherTables?.length]);

  useEffect(() => {
    setColumns(tableColumns);
  }, [profile, tableColumns]);

  useEffect(() => {
    const filtered = filteringTable(fetchedData || [], filtering, stateVariables);

    let sortedData = [...filtered];
    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredData(sortedData);
  }, [fetchedData, filtering, sortConfig, stateVariables]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        return { key, direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleFilterChange = (name, value) => {
    const newFilteringState = { ...filtering };
    if (!value) {
      delete newFilteringState[name];
    } else {
      newFilteringState[name] = value;
    }
    setFiltering(newFilteringState);
    setPage(1);
  };
  
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const getActionConfig = (action) => {
    if (typeof action === 'string') {
      return { label: action, action: action };
    }
    return { label: action?.label || action?.action, action: action?.action, tooltip: action?.tooltip, modalAction: action?.modalAction };
  };

  const handleCustomAction = (actionConfig, item) => {
    const { action, modalAction } = actionConfig || {};
    switch (action) {
      case 'addTo':
        toggleModal(true, `add-${table}-to-Organisation`, item);
        break;
      case 'viewOrg': {
        let link = `/organisations/${item.id}`;
        if (table === 'benchmark') {
          link = `/organisations/${item.org}`;
        } else if (table === 'organisationMetrics') {
          link = `/organisations/${item.organisationId}`;
        }
        history.push(link);
        break;
      }
      case 'editSBT': {
        const orgDetails = _.find(stateVariables?.adminOrgs, { id: item.organisation });
        setCurrentOrganisation(orgDetails);
        history.push(`/netZero/${item.id}`);
        break;
      }
      case 'link':
        if (table === 'reports') {
          history.push(`/report/${item.organisationId}/${item.id}`);
        }
        break;
      case 'recalculate':
        toggleModal(true, 'orgs-table-recalcOrgUsage', item);
        break;
      case 'clearCache':
        if (modalAction) {
          toggleModal(true, modalAction, item);
        }
        break;
      case 'syncPrices':
        toggleModal(true, 'addOnProducts-sync-prices', item);
        break;
      default:
        break;
    }
  };

  const renderTableCell = (item, column) => {
    const col = typeof column === 'string' ? column : column.key;
    const label = typeof column === 'string' ? column : column.label;
    const formattedValue = formatCellValue({
      table,
      dataKey: col,
      rowData: item,
      stateVariables,
      cellData: item[col],
      toggleModal,
      tableActions,
      label,
      getItemsByTermsFromIndex,
      searchableFields,
      setCurrentOrganisation,
      history,
    });

    if (formattedValue === undefined || formattedValue === null) return <span className='text-gray-400'>-</span>;

    if (typeof formattedValue === 'boolean') {
      return formattedValue ? (
        <Check size={16} strokeWidth={1.5} className='select-none cursor-default text-green-500' />
      ) : (
        <X size={16} strokeWidth={1.5} className='select-none cursor-default text-red-500' />
      );
    }

    return (
      <div className='truncate max-w-xs'>
        {formattedValue}
      </div>
    );
  };

  if (loading) {
    return (
      <Card className='w-full shadow-md'>
        <CardContent className='flex justify-center items-center p-8'>
          <Spinner className='h-8 w-8' />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='w-full shadow-md'>
      <CardHeader className='flex flex-col space-y-4 py-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0'>
        <CardTitle className='capitalize text-xl font-semibold' style={{ fontFamily: headers }}>
          {removeCamelCase(table) || 'Admin Portal Table'}
        </CardTitle>
        <div className='flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2'>
          {Object.keys(cleanDeep(stateVariables)).length > 5 &&
            tableFilters?.map((filter) => {
              const options = stateVariables?.[filter.options]?.map(itemToOptions) || filter.options;
              return (
                <div key={filter.name} className='relative'>
                  <Select
                    value={filtering[filter.name] ?? ''}
                    onValueChange={(value) => handleFilterChange(filter.name, value)}>
                    <SelectTrigger onClear={() => handleFilterChange(filter.name, '')} className='w-full sm:w-[180px]'>
                      <SelectValue placeholder={filter.label} />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.isArray(options) &&
                        options.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          <Input
            className='w-full sm:max-w-xs'
            placeholder='Search'
            name={searchableFields?.join('_') || 'text'}
            value={filtering[searchableFields?.join('_')] || ''}
            onChange={(e) => handleFilterChange(e.target.name, e.target.value)}
          />
        </div>
      </CardHeader>
      <CardContent className='p-0 mx-6'>
        {filteredData?.length < 1 ? (
          <Alert variant='warning' className='m-2 flex flex-col items-start gap-1.5 text-left'>
            <AlertTitle>No Match Found</AlertTitle>
            <AlertDescription>There are no search results!</AlertDescription>
          </Alert>
        ) : (
          <>
            <div className='border rounded-md overflow-x-auto'>
              <Table className='min-w-full'>
                <TableHeader>
                  <TableRow>
                    {columns?.map((column, index) => {
                      const colKey = column?.key || column;
                      const colLabel = column?.label || column;
                      return (
                        <TableHead key={index} className='text-center' onClick={() => handleSort(colKey)}>
                          <div className='flex items-center justify-center cursor-pointer'>
                            {colLabel}
                            {sortConfig.key === colKey && (
                              sortConfig.direction === 'asc' ? <ArrowUp size={14} strokeWidth={1.5} className='ml-1' /> : <ArrowDown size={14} strokeWidth={1.5} className='ml-1' />
                            )}
                          </div>
                        </TableHead>
                      );
                    })}
                    {tableActions && tableActions.length > 0 && (
                      <TableHead className='text-center'>Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedData.map((item, rowIndex) => (
                    <TableRow key={item.id || rowIndex}>
                      {columns?.map((column, colIndex) => (
                        <TableCell key={colIndex} className='text-center justify-items-center'>
                          {renderTableCell(item, column)}
                        </TableCell>
                      ))}
                      {tableActions && tableActions.length > 0 && (
                        <TableCell className='text-center'>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant='ghost' size='sm'>
                                <MoreHorizontal size={16} strokeWidth={1.5} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end'>
                              {tableActions.includes('view') && (
                                <DropdownMenuItem asChild>
                                  <Link to={`/admin/${table}/${item.id}`}>
                                    <Eye size={16} strokeWidth={1.5} className='mr-2' /> View
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              {tableActions.includes('edit') && (
                                <DropdownMenuItem onClick={() => toggleModal(true, `${table}-table-edit`, item)}>
                                  <Pencil size={16} strokeWidth={1.5} className='mr-2' /> Edit
                                </DropdownMenuItem>
                              )}
                              {tableActions.includes('delete') && (
                                <DropdownMenuItem onClick={() => toggleModal(true, `${table}-table-delete`, item)}>
                                  <Trash2 size={16} strokeWidth={1.5} className='mr-2' /> Delete
                                </DropdownMenuItem>
                              )}
                              {tableActions
                                .map(getActionConfig)
                                .filter(
                                  ({ action }) =>
                                    action &&
                                    !['view', 'edit', 'delete', 'add', 'downloadFile', 'exportUserTable', 'repackage'].includes(action)
                                )
                                .map((actionConfig, idx) => (
                                  <DropdownMenuItem
                                    key={`${actionConfig.action}-${idx}`}
                                    onClick={() => handleCustomAction(actionConfig, item)}>
                                    <MoreHorizontal size={16} strokeWidth={1.5} className='mr-2' />
                                    {actionConfig.label || convertToTitleCase(actionConfig.action)}
                                  </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className='mt-6 mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex items-center justify-center sm:justify-start text-sm text-gray-500'>
                Showing {startIndex + 1}-{Math.min(endIndex, filteredData.length)} of {filteredData.length}
              </div>

              <div className='flex flex-col gap-4 items-center sm:flex-row sm:items-center'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium'>Rows per page</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => {
                      setPageSize(Number(value));
                      setPage(1);
                    }}>
                    <SelectTrigger className='w-20'>
                      <SelectValue placeholder={pageSize} />
                    </SelectTrigger>
                    <SelectContent side='top'>
                      {[5, 10, 20, 50, 100].map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className='flex items-center gap-2'>
                  <div className='flex items-center justify-center text-sm font-medium mr-2'>
                    Page {page} of {totalPages}
                  </div>
                  <div className='flex items-center gap-1'>
                    <Button variant='ghost' className='h-8 w-8 p-0' onClick={() => handlePageChange(1)} disabled={page <= 1}>
                      <span className='sr-only'>Go to first page</span>
                      <ChevronsLeft size={16} strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant='ghost'
                      className='h-8 w-8 p-0'
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page <= 1}>
                      <span className='sr-only'>Go to previous page</span>
                      <ChevronLeft size={16} strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant='ghost'
                      className='h-8 w-8 p-0'
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= totalPages}>
                      <span className='sr-only'>Go to next page</span>
                      <ChevronRight size={16} strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant='ghost'
                      className='h-8 w-8 p-0'
                      onClick={() => handlePageChange(totalPages)}
                      disabled={page >= totalPages}>
                      <span className='sr-only'>Go to last page</span>
                      <ChevronsRight size={16} strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    tableDetails: {
      tableName: table,
      sortIndex,
      columns,
      actions,
      fetchDataFromOtherTables = [],
      tableFilters = [],
      tableData,
      searchableFields = [],
    } = {},
    stateVariables,
  } = ownProps || {};

  let fetchedData = [];

  if (tableData?.length) {
    fetchedData = _.sortBy(tableData, [sortIndex]);
  } else if (table === 'benchmark') {
    const benchmarkData = state?.admin?.adminPortal?.[table];
    fetchedData = benchmarkData?.map((bmk) => {
      const orgDetails = _.find(stateVariables['adminOrgs'], { id: bmk.org });
      const industry = _.find(stateVariables['industries'], { id: orgDetails?.industry });
      return {
        ...bmk,
        orgName: orgDetails?.name,
        industry: industry?.id,
        industryName: industry?.name,
        industryIncludes: industry?.include,
        parent: industry?.parent,
      };
    });
  } else if (table === 'userPermissions') {
    const userPermissions = state?.admin?.adminPortal?.[table];
    fetchedData = userPermissions?.map((userPerm) => {
      let profile = _.find(stateVariables['profile'], { id: userPerm.user });
      if (!profile) {
        profile = _.find(stateVariables['profile'], { cognitoId: userPerm.user });
      }
      return {
        ...userPerm,
        email: profile ? profile?.email : userPerm.email,
      };
    });
  } else if (table === 'countries') {
    const currencies = state?.admin?.adminPortal?.currencies;
    const countries = state?.admin?.adminPortal?.[table];
    fetchedData = countries?.map((country) => {
      const currency = _.find(currencies, { id: country.currency });
      return {
        ...country,
        currencyName: currency?.name,
      };
    });
  } else if (table === 'currencies') {
    const currencies = state?.admin?.adminPortal?.[table];
    fetchedData = currencies?.map((currency) => {
      if (currency?.hideInUI === undefined || currency?.hideInUI === null) {
        currency.hideInUI = false;
      }
      return {
        ...currency,
      };
    });
  } else {
    fetchedData = _.sortBy(state?.admin?.adminPortal?.[table], [sortIndex]) || state?.admin?.adminPortal?.[table];
  }

  if (searchableFields.length > 0) {
    const searchableKey = searchableFields.join('_');
    const newItem = fetchedData?.map((item) => {
      return { ...item, [searchableKey]: searchableFields.map((field) => item[field]).join('_') };
    });
    fetchedData = newItem;
  }

  return {
    fetchedData,
    branding: state.branding,
    table,
    tableColumns: columns,
    tableActions: actions,
    fetchDataFromOtherTables,
    tableFilters,
    searchableFields,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getAllItemsFromDB: (table) => dispatch(ADMINACTIONS.getAllItemsFromDB(table)),
  toggleModal: (status, action, item, notAutoClose) =>
    dispatch(ADMINACTIONS.toggleModal(status, action, item, notAutoClose)),
  getItemsByTermsFromIndex: (index, filters, page, size) =>
    dispatch(ADMINACTIONS.getItemsByTermsFromIndex(index, filters, page, size)),
  adminPortalItems: (items, table) => dispatch(ADMINACTIONS.adminPortalItems(items, table)),
  setCurrentOrganisation: (organisation) => dispatch(ORGANISATIONACTIONS.setCurrentOrganisation(organisation)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminPortalTable);
